import { clear, createStore, get, set } from "idb-keyval";
import { primarySync } from "./deltaSync";

// Device-local copy of each primaries collection, so a reload only has to fetch what changed
// since the last sync rather than re-reading every primary from Firestore. One entry per
// collection: { version, owner, accounts, lastSync, docs }.
//
// IndexedDB can be missing or throw (private windows, blocked site data, iOS evicting it), so
// every call here fails soft - the worst case is a full re-read from Firestore.

// Bump when the cached shape changes - older entries are then ignored and rebuilt.
const CACHE_VERSION = 1;

let store = null;

const getStore = () => {
  if (!store) store = createStore("hes-primaries-cache", "collections");
  return store;
};

// accountsKey ties the cache to who it was built for - a newly linked account's older
// primaries would never arrive through the "changed since" listener, so a change of user or
// linked accounts means starting again.
export const accountsKeyFor = (accounts = []) => [...accounts].sort().join(",");

export const readPrimaryCache = async (collection, owner, accountsKey) => {
  try {
    let entry = await get(collection, getStore());
    if (
      !entry ||
      entry.version !== CACHE_VERSION ||
      entry.owner !== owner ||
      entry.accounts !== accountsKey
    ) {
      return null;
    }
    return entry;
  } catch (err) {
    console.warn("Primaries cache unavailable, falling back to a full read", err);
    return null;
  }
};

export const writePrimaryCache = async (collection, owner, accountsKey, lastSync, docs) => {
  try {
    await set(
      collection,
      { version: CACHE_VERSION, owner, accounts: accountsKey, lastSync, docs },
      getStore()
    );
  } catch (err) {
    console.warn("Unable to save primaries cache", err);
  }
};

export const clearPrimaryCache = async () => {
  try {
    await clear(getStore());
  } catch (err) {
    console.warn("Unable to clear primaries cache", err);
  }
};

// Settings' "Re-sync all data", and sign-out: drops what every collection holds, wipes this
// device's cache, and (while signed in) re-reads everything. The resync event goes first so
// no collection can flush its pending cache write after the clear.
export const resetPrimaries = async () => {
  primarySync.requestResync();
  await clearPrimaryCache();
};
